import React, { FC, useState, useEffect, useCallback, useMemo } from 'react'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router-dom'
import { IonPage, IonContent, useIonViewWillEnter } from '@ionic/react'
import { memberMobx, memberCardMobx } from 'mobx/mkt'
import { format, toPrice, toPriceNoCurrency, toCurrency, times } from '@dt/fe-utils'
import { flatMap, forEach, groupBy, keyBy, map, uniqBy } from '@dt/fe-utils/dist/lodash'
import { useHistoryNew, getCouponsInfo, modalLayer, toast, getPlatform } from '@utils'
import { CouponItem, UseHeader, MktFooter, CouponDetail, TipModal, PayModal } from '@cps'
import { CdShow, DetailProgress, TermsTick, ChooseStore, StoreListModal } from 'pages/mkt/components'
import { Stepper } from 'components'
import { ActivityAPI, MemberApi } from 'api/index'
import mktController from 'controllers/mktController'
import RedeemSDK from 'sdk/redeem'
import './index.scss'

import defaultImg from '@img/mkt/default-redeem.jpg'
import { TGroupByProductType } from '@common/enum/mkt.enum'

interface IProps extends IPage, RouteComponentProps { }

const { isWeixinMiniProgram } = getPlatform()

const ProductDetail: FC<IProps> = ({ match }: IProps) => {
  const history = useHistoryNew()
  const { productId } = match.params as any
  const [info, setInfo] = useState<any>({})
  const [statusInfo, setStatusInfo] = useState<any>({})
  const [scrollTop, setScrollTop] = useState<number>(0)
  const [couponMap, setCouponMap] = useState<any>({})
  const [isLimit, setIslimit] = useState<boolean>(false)
  const [isAgree, setIsAgree] = useState<boolean>(false)
  const [clause, setClause] = useState<IClauseInfo>(null)
  const [showTip, setShowTip] = useState<boolean>(false)
  const [cnt, setCnt] = useState(1)
  const [boughtNum, setBoughtNum] = useState(0) // 已购买的团购商城数量
  const [storeInfo, setStoreInfo] = useState<IStoreInfo>({ storeId: 0, storeName: '' })
  /** 是否是普通团购券 */
  const isDefaultProductTypeCoupon = useMemo(() => {
    return !info.productType || info.productType === TGroupByProductType.coupons
  }, [info.productType])

  const buyProduct = () => {
    if (Object.keys(info).length === 0) return
    if (!isAgree && clause?.clauseName) {
      setShowTip(true)
      return
    }
    if (memberMobx.brandConfig?.isSelectStoreWhenRedeemCoupon && !storeInfo.storeId) {
      modalLayer(StoreListModal, { showIds: null }, 'StoreListModal').then((val: any) => {
        setStoreInfo({ storeId: val.storeId, storeName: val.storeName })
        handBuy(val.storeId)
      }).catch(() => { })
    } else {
      handBuy(storeInfo.storeId)
    }
  }
  const handBuy = (storeId?: number) => {
    const cateNo: string = 'redeemCouponByMoney'
    const orderList: any = [{
      type: cateNo,
      cnt,
      productCategoryNo: cateNo,
      productNo: info.redeemProductId,
      productName: info.productName,
      productScaleNo: '',
      paidFee: times(info.productMoney, cnt),
      originalFee: times(info.productMoney, cnt)
    }]
    const orderParams: any = {
      totalFee: times(info.productMoney, cnt),
      paidFee: times(info.productMoney, cnt),
      details: orderList,
      eatType: cateNo,
      contactInfo: '',
      storeId: storeId || 0,
    }
    modalLayer(PayModal, { scene: 'coupons_buy', orderPara: orderParams, productType: info.productType, history })
  }
  const initData = () => {
    setStatusInfo(RedeemSDK.getInfo(info))
  }

  const getData = () => {
    ActivityAPI.getGroupBuyProductDetail(parseInt(productId)).then((res: any) => {
      if (!res.error) {
        setInfo(res || {})
      }
    })
  }
  const changeTermsStatus = (status: boolean) => {
    if (status) setShowTip(false)
    setIsAgree(status)
  }
  // 修改数量
  const handleChangeNum = useCallback((isAdd: boolean) => {
    if (isAdd && info.personLimit && (boughtNum + cnt) >= info.personLimit) {
      toast({ message: i18n.t('mkt.groupBuy.singleLimitTip', { num: info.personLimit }) })
      return
    }
    setCnt(prev => isAdd ? prev + 1 : prev - 1)
  }, [cnt, info, boughtNum])
  const handleEditCnt = useCallback((value: number) => {
    if (info.personLimit && value > info.personLimit) {
      if (info.personLimit - boughtNum !== cnt) {
        setCnt(info.personLimit - boughtNum)
      }
      toast({ message: i18n.t('mkt.groupBuy.singleLimitTip', { num: info.personLimit }) })
      return
    }
    setCnt(value)
  }, [cnt, info, boughtNum])
  // Component Effect
  useIonViewWillEnter(() => {
    mktController.checkLogin(history).then(() => {
      getData()
    })
  })

  useEffect(() => {
    if (Object.keys(info).length > 0) {
      initData()
      MemberApi.redeemProductOrders({
        redeemProductId: info.redeemProductId
      }).then((res: any) => {
        if (!res.error) {
          if (info.personLimit && parseInt(res.meta.totalProductCnt) >= info.personLimit) {
            setIslimit(true)
          }
          setBoughtNum(parseInt(res.meta.totalProductCnt))
        }
      })
      ActivityAPI.getCouponsList({
        couponIds: getCouponsInfo(info.coupons)
      }).then((res: any) => {
        if (!res.error) {
          setCouponMap(keyBy(res, 'couponId'))
        }
      })
    }
    mktController.initShareData({
      url: isWeixinMiniProgram ? `/pages/launch/index?jumpH5Page=${encodeURIComponent(location.href)}` : location.href,
      title: i18n.t('mkt.groupBuy.groupBuy2'),
      img: info.productImg || defaultImg,
    })
  }, [info])
  useEffect(() => {
    if (memberMobx.brandConfig?.clauses?.redeemCouponByMoney?.status === 'enable') {
      setClause(memberMobx.brandConfig.clauses.redeemCouponByMoney)
      setIsAgree(memberMobx.brandConfig.clauses.redeemCouponByMoney.isDefaultCheck)
    }
  }, [memberMobx.brandConfig])
  useEffect(() => {
    memberCardMobx.getCardList()
    memberCardMobx.getLevelCardList()
  }, [])
  const showExpireTime = statusInfo.expired > 0 && statusInfo.expired <= 3
  const hasSoldOut = statusInfo.status === 2 // 商品售磬
  const showSoldNum = !hasSoldOut && memberMobx?.brandConfig?.isShowRedeemMoneySale // 商品已售数量
  const { groupBuyEnable, groupBuyOnlyTip } = mktController.checkGroupBuyAuth(info)
  const showTopTime = ![5, 6, 7].includes(statusInfo.status)
  return (
    <IonPage
      className='groupBuy-detail-modal'
    >
      {scrollTop > 100 && <UseHeader title={info.productName} onBack={() => mktController.goBack(history)} animation> </UseHeader>}
      {
        scrollTop <= 100 &&
          <div data-cypid='mmgGroupBuyBack' className='top-back' onClick={() => mktController.goBack(history)}>
            <i className='iconfont icon-BACK-12x'></i>
          </div>
      }
      <IonContent
        class='modal-content'
        scrollEvents
        forceOverscroll
        onIonScroll={(e: any) => {
          setScrollTop(e.detail.scrollTop)
        }}
      >
        <div className='modal-top'>
          <div className='top-area' style={{ backgroundImage: `Url(${info.productImg || defaultImg})` }}></div>
        </div>
        <div className='pro-con'>
          {showTopTime &&
            <div className='top-time'>
              <DetailProgress rate={statusInfo.saleRate}></DetailProgress>
              <div className='bar-info'>
                <div className='left'>
                  <span className='left-text'>
                    {statusInfo.status === 0 && i18n.t('mkt.redeem.timeRob', { time: format.toTime(statusInfo.saleStart) })}
                    {statusInfo.status === 1 && i18n.t('mkt.redeem.panicBuying')}
                    {statusInfo.status === 2 && i18n.t('mkt.redeem.soldOut')}
                    {statusInfo.status === 3 && i18n.t('mkt.redeem.nextGrabbing')}
                    {statusInfo.status === 4 && i18n.t('mkt.redeem.endThisRound')}
                    {/* {statusInfo.status === 5 && `${i18n.t('mkt.redeem.sold')} ${statusInfo.type === 0 ? statusInfo.allSaleCnt : statusInfo.saleCnt}`} */}
                  </span>
                  {/* 待售 在售 售罄(不是总量售卖) 本轮结束(可售卖) */}
                  {
                    statusInfo.status === 0 || statusInfo.status === 1 || (statusInfo.status === 2 && statusInfo.type === 2) || statusInfo.status === 3
                      ? (
                        <div className='left-time'>
                          <CdShow
                            time={statusInfo.cdTime} onEnd={() => {
                              initData()
                            }}
                            onlyTime={false}
                          >
                          </CdShow>
                        </div>) : ''
                  }
                </div>
                <div className='right'>
                  {
                    statusInfo.type !== 0
                      ? (
                        <>
                          {statusInfo.status === 0 || statusInfo.status === 3 ? i18n.t('mkt.redeem.limitSaleCnt', { cnt: statusInfo.stockCnt }) : ''}
                          {statusInfo.status === 2 && i18n.t('mkt.redeem.endThisRound')}
                          {statusInfo.status === 1 && i18n.t('mkt.redeem.onlyLeftCnt', { cnt: statusInfo.remainCnt })}
                          {[4, 5].includes(statusInfo.status) &&
                            <>
                              {
                                statusInfo.expired > 0 && statusInfo.expired <= 3 ? i18n.t('mkt.redeem.someDaysOff', { day: statusInfo.expired }) : ''
                              }
                            </>}
                        </>)
                      : (
                        <>
                          {statusInfo.status === 3 && i18n.t('mkt.redeem.endThisRound')}
                          {/* {statusInfo.status !== 3 &&
                            <>
                              {
                                statusInfo.expired > 0 && statusInfo.expired <= 3 ? i18n.t('mkt.redeem.someDaysOff', { day: statusInfo.expired }) : ''
                              }
                            </>} */}
                        </>)
                  }
                </div>
              </div>
            </div>}
          {groupBuyOnlyTip && (
            <div className={`member-card-auth${!showTopTime ? ' member-card-auth-top-blank' : ''}`}>
              <div className='member-card-auth-text'>{groupBuyOnlyTip}</div>
            </div>
          )}
          <div className='top-info-container'>
            <div className='top-info'>
              <div className='name'>{info.productName}</div>
              {
                (showExpireTime || showSoldNum) &&
                  <div className='sale'>
                    {showSoldNum && (
                      `${i18n.t('mkt.redeem.sold')} ${statusInfo.type === 0 ? statusInfo.allSaleCnt : statusInfo.saleCnt}`
                    )}
                    {showExpireTime && `${showSoldNum ? '，' : ''}${i18n.t('mkt.redeem.someDaysOff', { day: statusInfo.expired })}`}
                  </div>
              }
              <div className='price'>
                <div className='price-info'>
                  <span className='currency'>{toCurrency()} </span>
                  <span>{toPriceNoCurrency(info.productMoney)}</span>
                </div>
                {!isDefaultProductTypeCoupon && <div className='limit-text'>{i18n.t(`mkt.groupBuy.${info.productType}`)}</div>}
                {info.personLimit && <div className='limit-text'>{i18n.t('mkt.groupBuy.singleLimit', { num: info.personLimit })}</div>}
              </div>
            </div>
            {
              isDefaultProductTypeCoupon && (info.coupons || []).map((item: any) => {
                const couponInfo: any = couponMap[item.couponId]
                if (couponInfo) {
                  return (
                    <div className='coupon-block' key={item.couponId}>
                      <CouponItem
                        type='get'
                        cnt={item.couponCnt}
                        couponInfo={couponInfo}
                        onDetail={() => {
                          modalLayer(CouponDetail, { type: 'get', couponInfo: couponInfo }, 'couponDetail')
                        }}
                      >
                      </CouponItem>
                    </div>)
                }
              })
            }
            {
              !isDefaultProductTypeCoupon && (info.childProducts || []).map((prod) => (
                <div className='child-product-item-wrapper' key={prod.redeemProductId}>
                  <div className='child-product-name'>/{prod.productName}/</div>
                  {
                    (prod.coupons || []).map((item: any) => {
                      const couponInfo: any = couponMap[item.couponId]
                      if (couponInfo) {
                        return (
                          <div className='coupon-block' key={item.couponId}>
                            <CouponItem
                              type='get'
                              cnt={item.couponCnt}
                              couponInfo={couponInfo}
                              onDetail={() => {
                                modalLayer(CouponDetail, { type: 'get', couponInfo: couponInfo }, 'couponDetail')
                              }}
                            >
                            </CouponItem>
                          </div>)
                      }
                    })
                  }
                </div>
              ))
            }

            {
              info.productIntroduction &&
                <>
                  <div className='title'>{i18n.t('mkt.redeem.productContent')}</div>
                  <div className='intro'>{info.productIntroduction}</div>
                </>
            }
            {
              info.product !== TGroupByProductType.coupons && !info.isRefund &&
                <>
                  <div className='intro' style={{ marginTop: 5 }}>⚠️ {i18n.t('mkt.groupBuy.wranBuyDesc')}</div>
                </>
            }
          </div>
        </div>
      </IonContent>
      {/* 在售 全时段售卖 */}
      {
        (statusInfo.status === 1 || statusInfo.status === 5) && (groupBuyEnable
          ? (
            <MktFooter className='footer-btn-wrap'>
              <div className='footer-block'>
                {
                  clause?.clauseName &&
                    <div className='amount-title'>
                      <TermsTick terms={clause} isCheck={isAgree} showTip={showTip} onChange={(check: boolean) => changeTermsStatus(check)}></TermsTick>
                    </div>
                }
                {
                  memberMobx.brandConfig?.isSelectStoreWhenRedeemCoupon &&
                    <div className='amount-title'>
                      <ChooseStore scene='groupBuy' openChoose={!!memberMobx.brandConfig.isSelectStoreWhenRedeemCoupon} detailInfo={{ isAllStore: true }} storeInfo={storeInfo} onChange={(info: any) => setStoreInfo(info)}></ChooseStore>
                    </div>
                }
                <div className='groupBuy-detail-modal-footer'>
                  {!isLimit && <Stepper min={1} max={999} cnt={cnt} size='large' onChange={handleChangeNum} onEdit={handleEditCnt} />}
                  <div
                    data-cypid='mmgBuyGroupBuy'
                    className={`add-cart ${isLimit ? 'disable' : ''}`}
                    onClick={() => {
                      if (isLimit) return
                      buyProduct()
                    }}
                  >
                    <div>{isLimit ? i18n.t('mkt.groupBuy.canNotBuy', { num: info.personLimit || 0 }) : i18n.t('mkt.groupBuy.payPrice', { price: toPrice(times(info.productMoney, cnt)) })}</div>
                  </div>
                </div>
              </div>

            </MktFooter>)
          : (
            <MktFooter className='footer-btn-wrap'>
              <div className='no-card-auth'>{i18n.t('mkt.redeem.mCardDisable')}</div>
            </MktFooter>
          )
        )
      }
    </IonPage>
  )
}

export default observer(ProductDetail)
